import { useEffect, useState } from "react";

import { ApiError, apiClient } from "../../api/client";
import type { EarningBand, EarningCurve, MeResponse } from "../../api/types";
import { hasPermission } from "../../auth/permissions";
import { useIdempotencyKey } from "../../lib/idempotencyKey";

const DEFAULT_BANDS: EarningBand[] = [
  { from_pct: 0, to_pct: 60, rate: 0 },
  { from_pct: 60, to_pct: null, rate: 1 }
];

const README_BANDS: EarningBand[] = [
  { from_pct: 0, to_pct: 60, rate: 0 },
  { from_pct: 60, to_pct: 80, rate: 0.75 },
  { from_pct: 80, to_pct: 101, rate: 1 },
  { from_pct: 101, to_pct: 110, rate: 1.1 },
  { from_pct: 110, to_pct: 125, rate: 1.25 },
  { from_pct: 125, to_pct: 150, rate: 1.5 },
  { from_pct: 150, to_pct: null, rate: 2 }
];

interface State {
  status: "loading" | "ready" | "error";
  // payout_start_pct/configured reflect the last successfully loaded/saved
  // curve — the derived labels read from these, not from the editable bands.
  payoutStartPct: number;
  configured: boolean;
  errorMessage: string | null;
}

const initial: State = {
  status: "loading",
  payoutStartPct: 0,
  configured: false,
  errorMessage: null
};

export function EarningCurvePage({ me }: { me: MeResponse }) {
  const [state, setState] = useState<State>(initial);
  const [bands, setBands] = useState<EarningBand[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const { keyFor, reset } = useIdempotencyKey();

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const curve = await apiClient.getEarningCurve();
        if (cancelled) {
          return;
        }
        setBands(curve.bands);
        setState({
          status: "ready",
          payoutStartPct: curve.payout_start_pct,
          configured: curve.configured,
          errorMessage: null
        });
      } catch (caught) {
        if (cancelled) {
          return;
        }
        setState({
          status: "error",
          payoutStartPct: 0,
          configured: false,
          errorMessage: messageFor(caught)
        });
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const canSave = hasPermission(me, "blocks:earning:configure");

  function applyCurve(curve: EarningCurve) {
    setBands(curve.bands);
    setState((previous) => ({
      ...previous,
      payoutStartPct: curve.payout_start_pct,
      configured: curve.configured
    }));
  }

  function editBands(next: EarningBand[]) {
    setBands(next);
    setSaved(false);
    setActionError(null);
  }

  function updateBand(index: number, patch: Partial<EarningBand>) {
    editBands(bands.map((band, i) => (i === index ? { ...band, ...patch } : band)));
  }

  function addBand() {
    editBands([...bands, { from_pct: 0, to_pct: null, rate: 0 }]);
  }

  function removeBand(index: number) {
    editBands(bands.filter((_, i) => i !== index));
  }

  async function save() {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    setActionError(null);
    setSaved(false);
    try {
      const curve = await apiClient.putEarningCurve(bands, keyFor(JSON.stringify(bands)));
      applyCurve(curve);
      setSaved(true);
      reset();
    } catch (caught) {
      // The server replays a stored answer for a reused key, so a refused
      // request needs a new key to be retried; a network error may never have
      // reached the server, so that retry keeps the key.
      if (caught instanceof ApiError) {
        reset();
      }
      setActionError(messageFor(caught));
    } finally {
      setSubmitting(false);
    }
  }

  if (state.status === "loading") {
    return <p className="loading">Loading earning curve…</p>;
  }
  if (state.status === "error") {
    return (
      <div className="card">
        <h2>Couldn't load earning curve</h2>
        <p>{state.errorMessage}</p>
      </div>
    );
  }

  return (
    <div className="blocks-view">
      <section className="card">
        <h2>Earning curve</h2>
        <p className="muted">
          Bands map block progress to an earning rate. Each band runs from its start percent up to
          (but not including) the next; the open band has no upper bound.
        </p>
        <p>
          <span className="chip chip--impact">Blocks start earning at {state.payoutStartPct}%</span>
        </p>
        {!state.configured && (
          <p className="muted">Using the default curve (no custom configuration yet).</p>
        )}
      </section>

      <section className="card">
        <h3>Bands</h3>
        <table className="people-table band-table">
          <thead>
            <tr>
              <th className="people-table__th people-table__th--left">From %</th>
              <th className="people-table__th people-table__th--left">To %</th>
              <th className="people-table__th people-table__th--left">Rate</th>
              <th className="people-table__th people-table__th--right" />
            </tr>
          </thead>
          <tbody>
            {bands.map((band, index) => (
              <tr key={index}>
                <td>
                  <input
                    type="number"
                    value={band.from_pct}
                    onChange={(event) => updateBand(index, { from_pct: Number(event.target.value) })}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    value={band.to_pct ?? ""}
                    placeholder="∞"
                    onChange={(event) =>
                      updateBand(index, {
                        to_pct: event.target.value === "" ? null : Number(event.target.value)
                      })
                    }
                  />
                </td>
                <td>
                  <input
                    type="number"
                    step={0.05}
                    min={0}
                    value={band.rate}
                    onChange={(event) => updateBand(index, { rate: Number(event.target.value) })}
                  />
                </td>
                <td className="people-table__num">
                  <button type="button" className="btn btn--ghost" onClick={() => removeBand(index)}>
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="form-row form-row--inline">
          <button type="button" className="btn btn--ghost" onClick={addBand}>
            Add band
          </button>
          <button type="button" className="btn btn--ghost" onClick={() => editBands(DEFAULT_BANDS)}>
            Load default
          </button>
          <button type="button" className="btn btn--ghost" onClick={() => editBands(README_BANDS)}>
            Load README preset
          </button>
        </div>

        <div className="form-row form-row--inline">
          <button
            type="button"
            className="btn btn--primary"
            disabled={!canSave || submitting}
            onClick={() => void save()}
          >
            {submitting ? "Saving…" : "Save curve"}
          </button>
          {saved && <span className="chip chip--impact">Saved</span>}
          {actionError !== null && <p className="form-row__error">{actionError}</p>}
        </div>
      </section>
    </div>
  );
}

function messageFor(caught: unknown): string {
  if (caught instanceof ApiError) {
    return caught.message;
  }
  return caught instanceof Error ? caught.message : "Could not load earning curve.";
}
